import { useState } from 'react';
import { Box, Button, Typography, Paper, CircularProgress } from '@mui/material';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import dayjs from 'dayjs';
import { usePicklists } from '@lingaai/shared-hooks';
import { useCurrentUser } from '@lingaai/user-management';
import { createNode, createEdge } from '@lingaai/supabase-client';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import type { CreateNodeInput } from '@lingaai/types';
import { NODE_REGISTRY as NODE_TYPE_CONFIG } from '@lingaai/types/graph/nodes';
import { useToast } from '../../contexts/ToastContext';
import { FormField } from '../forms/FormField';
import { EntityMultiSelect } from '../forms/EntityMultiSelect';

interface RelatedEntity {
  id: string;
  type: string;
  name: string;
}

interface ChatNodeFormProps {
  nodeType: string;
  prefill?: Record<string, unknown>;
  onSuccess?: (node: unknown, message: string) => void;
  onCancel?: () => void;
}

/**
 * Inline form for creating nodes within the chat interface.
 * Driven by NODE_TYPE_CONFIG.
 */
export function ChatNodeForm({ nodeType, prefill = {}, onSuccess, onCancel }: ChatNodeFormProps) {
  const { data: currentUser } = useCurrentUser();
  const { showToast } = useToast();
  const queryClient = useQueryClient();
  const { data: picklists } = usePicklists();
  const nodeConfig = NODE_TYPE_CONFIG[nodeType];

  // Initialize form with prefill values
  const initialData: Record<string, unknown> = {};
  nodeConfig?.formFields.forEach((field) => {
    initialData[field.key] = prefill[field.key] ?? '';
  });
  const [formData, setFormData] = useState<Record<string, unknown>>(initialData);

  // Related entities for Activity nodes (and potentially others)
  const initialRelatedEntities = (prefill.relatedEntities as RelatedEntity[]) || [];
  const [relatedEntities, setRelatedEntities] = useState<RelatedEntity[]>(initialRelatedEntities);
  const showRelatedEntities = nodeType === 'Activity';

  const createMutation = useMutation({
    mutationFn: async (input: CreateNodeInput) => {
      const newNode = await createNode(input);

      // Create RELATED_TO edges for each related entity
      if (relatedEntities.length > 0 && currentUser?.tenant_id) {
        await Promise.all(
          relatedEntities.map((entity) =>
            createEdge({
              type: 'RELATED_TO',
              src: newNode.id,
              dst: entity.id,
              props: {},
              tenant_id: currentUser.tenant_id!,
            })
          )
        );
      }

      return newNode;
    },
    onSuccess: (newNode) => {
      queryClient.invalidateQueries({ queryKey: ['nodes', newNode.type] });
      // Also invalidate relationship queries for linked entities
      relatedEntities.forEach((entity) => {
        queryClient.invalidateQueries({ queryKey: ['node-with-relationships', entity.id] });
      });
      const nodeName = (newNode.props as Record<string, unknown>)?.name || (newNode.props as Record<string, unknown>)?.subject || nodeConfig?.displayName;
      const message = `Created ${nodeConfig?.displayName} "${nodeName}"`;
      showToast(message, 'success');
      onSuccess?.(newNode, message);
    },
    onError: (error: any) => {
      const details = error.details?.validationErrors;
      if (details && Array.isArray(details)) {
        const messages = details.map((e: any) => {
          const field = e.path?.join('.') || 'field';
          return `${field}: ${e.message}`;
        });
        showToast(`Failed to create: ${messages.join(', ')}`, 'error');
      } else {
        showToast(`Failed to create: ${error.message}`, 'error');
      }
    },
  });

  if (!nodeConfig) {
    return (
      <Paper sx={{ p: 2, bgcolor: 'error.dark' }}>
        <Typography color="error">Unknown node type: {nodeType}</Typography>
      </Paper>
    );
  }

  const handleSubmit = () => {
    if (!currentUser?.tenant_id) return;

    const props: Record<string, unknown> = {};
    nodeConfig.formFields.forEach((field) => {
      const value = formData[field.key];
      if (value !== '' && value !== null && value !== undefined) {
        if (field.type === 'number' && typeof value === 'string') {
          props[field.key] = parseFloat(value);
        } else if (field.type === 'datetime' && typeof value === 'string') {
          // Normalize datetime to ISO 8601 with Z suffix
          props[field.key] = dayjs(value).toISOString();
        } else {
          props[field.key] = value;
        }
      }
    });

    createMutation.mutate({
      type: nodeType,
      props,
      tenant_id: currentUser.tenant_id,
    });
  };

  const handleChange = (key: string, value: unknown) => {
    setFormData((prev) => ({ ...prev, [key]: value }));
  };

  const getPicklistOptions = (picklistName?: string): Array<{ id: string; label: string }> | undefined => {
    if (!picklistName || !picklists) return undefined;
    const picklist = picklists.find((p) => p.name === picklistName);
    return picklist?.items;
  };

  return (
    <LocalizationProvider dateAdapter={AdapterDayjs}>
      <Paper sx={{ p: 2, bgcolor: 'background.paper', borderRadius: 2, mt: 1 }}>
        <Typography variant="subtitle2" sx={{ mb: 2, fontWeight: 600 }}>
          New {nodeConfig.displayName}
        </Typography>

        {nodeConfig.formFields.map((field) => (
          <Box key={field.key} sx={{ mb: 1.5 }}>
            <FormField field={field} value={formData[field.key] ?? ''} onChange={(value) => handleChange(field.key, value)} picklistOptions={getPicklistOptions(field.picklistName)} />
          </Box>
        ))}

        {showRelatedEntities && (
          <Box sx={{ mb: 1.5 }}>
            <EntityMultiSelect value={relatedEntities} onChange={setRelatedEntities} label="Related To" placeholder="Search people, organizations, deals..." />
          </Box>
        )}

        <Box sx={{ display: 'flex', gap: 1, mt: 2 }}>
          <Button variant="contained" size="small" onClick={handleSubmit} disabled={createMutation.isPending}>
            {createMutation.isPending ? <CircularProgress size={16} /> : 'Create'}
          </Button>
          {onCancel && (
            <Button variant="outlined" size="small" onClick={onCancel}>
              Cancel
            </Button>
          )}
        </Box>
      </Paper>
    </LocalizationProvider>
  );
}
